import { PatientLayout } from "@/components/layout/patient-layout";
import { useListConditions } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { UkHealthDisclaimer } from "@/components/ui/uk-health-disclaimer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Link } from "wouter";
import {
  Search,
  ArrowRight,
  Info,
  Flame,
  Activity,
  Gem,
  Layers,
  Droplets,
  LayoutGrid,
  ArrowUpDown,
} from "lucide-react";
import { useMemo } from "react";
import { useSearchParam } from "@/hooks/use-search-param";
import { useScrollRestore } from "@/hooks/use-scroll-restore";
import { Highlight } from "@/components/ui/highlight";

// ---------------------------------------------------------------------------
// Type config
// ---------------------------------------------------------------------------
const CONDITION_TYPES: {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  badgeColor: string;
  tabActive: string;
}[] = [
  {
    key: "",
    label: "All",
    description: "",
    icon: <LayoutGrid className="h-3.5 w-3.5" />,
    color: "",
    badgeColor: "",
    tabActive: "bg-rose-600 text-white border-rose-600",
  },
  {
    key: "inflammatory_arthritis",
    label: "Inflammatory Arthritis",
    description: "Conditions where the immune system attacks the joints",
    icon: <Flame className="h-3.5 w-3.5" />,
    color: "bg-rose-50 text-rose-700 border-rose-200",
    badgeColor: "bg-rose-100 text-rose-700 border-rose-200",
    tabActive: "bg-rose-600 text-white border-rose-600",
  },
  {
    key: "connective_tissue",
    label: "Connective Tissue",
    description: "Conditions affecting multiple body systems",
    icon: <Layers className="h-3.5 w-3.5" />,
    color: "bg-purple-50 text-purple-700 border-purple-200",
    badgeColor: "bg-purple-100 text-purple-700 border-purple-200",
    tabActive: "bg-purple-600 text-white border-purple-600",
  },
  {
    key: "vasculitis",
    label: "Vasculitis",
    description: "Inflammation of blood vessels",
    icon: <Activity className="h-3.5 w-3.5" />,
    color: "bg-amber-50 text-amber-700 border-amber-200",
    badgeColor: "bg-amber-100 text-amber-700 border-amber-200",
    tabActive: "bg-amber-600 text-white border-amber-600",
  },
  {
    key: "crystal_arthropathy",
    label: "Crystal Arthropathy",
    description: "Conditions caused by crystals in joints",
    icon: <Gem className="h-3.5 w-3.5" />,
    color: "bg-teal-50 text-teal-700 border-teal-200",
    badgeColor: "bg-teal-100 text-teal-700 border-teal-200",
    tabActive: "bg-teal-600 text-white border-teal-600",
  },
  {
    key: "other",
    label: "Other",
    description: "Other conditions managed by rheumatology",
    icon: <Droplets className="h-3.5 w-3.5" />,
    color: "bg-slate-50 text-slate-600 border-slate-200",
    badgeColor: "bg-slate-100 text-slate-600 border-slate-200",
    tabActive: "bg-slate-700 text-white border-slate-700",
  },
];

const TYPE_BY_KEY = Object.fromEntries(CONDITION_TYPES.filter(t => t.key).map(t => [t.key, t]));

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function PatientConditions() {
  useScrollRestore("patient-conditions");
  const [search, setSearch] = useSearchParam("q", "");
  const [activeType, setActiveType] = useSearchParam("type", "", { storageKey: "patient-conditions:type" });
  const [sort, setSort] = useSearchParam("sort", "az", { storageKey: "patient-conditions:sort" });
  const { data: conditions, isLoading } = useListConditions({ audience: "patient" });

  const filtered = useMemo(() => {
    if (!conditions) return [];
    const q = search.toLowerCase();
    const base = conditions.filter(c => {
      const matchesType = !activeType || (c.conditionType ?? "other") === activeType;
      const matchesSearch =
        !search ||
        c.name.toLowerCase().includes(q) ||
        (c.simpleName ?? "").toLowerCase().includes(q) ||
        (c.patientDescription ?? "").toLowerCase().includes(q);
      return matchesType && matchesSearch;
    });
    if (sort === "za") return [...base].sort((a, b) => a.name.localeCompare(b.name) * -1);
    return [...base].sort((a, b) => a.name.localeCompare(b.name));
  }, [conditions, search, activeType, sort]);

  const countByType = useMemo(() => {
    if (!conditions) return {};
    const counts: Record<string, number> = {};
    for (const c of conditions) {
      const t = c.conditionType ?? "other";
      counts[t] = (counts[t] ?? 0) + 1;
    }
    return counts;
  }, [conditions]);

  if (isLoading) return <PatientLayout title="Understanding Conditions"><LoadingScreen /></PatientLayout>;

  const activeTypeDef = activeType ? TYPE_BY_KEY[activeType] : null;

  return (
    <PatientLayout title="Understanding Conditions">
      {/* Header */}
      <div className="mb-8 max-w-3xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-rose-100 p-2.5 rounded-2xl shrink-0 mt-0.5">
            <Info className="h-6 w-6 text-rose-600" />
          </div>
          <p className="text-lg text-slate-600 leading-relaxed">
            Learn about different rheumatic conditions in plain, simple language. If you have questions about your specific diagnosis, always talk to your healthcare team.
          </p>
        </div>
        <UkHealthDisclaimer compact />
      </div>

      {/* Search */}
      <div className="mb-5 max-w-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search conditions…"
            className="pl-9"
          />
        </div>
      </div>

      {/* Type filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CONDITION_TYPES.map(t => {
          const count = t.key ? (countByType[t.key] ?? 0) : (conditions?.length ?? 0);
          if (t.key && count === 0) return null;
          const isActive = activeType === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveType(t.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                isActive
                  ? t.tabActive + " shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:border-rose-300 hover:text-rose-700"
              }`}
            >
              {t.icon}
              {t.label}
              <span className={`text-xs rounded-full px-1.5 py-0.5 ${isActive ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Active type description */}
      {activeTypeDef && (
        <div className={`mb-6 px-4 py-3 rounded-xl border text-sm ${activeTypeDef.color}`}>
          <span className="font-semibold">{activeTypeDef.label}:</span> {activeTypeDef.description}
        </div>
      )}

      {/* Results count + sort */}
      <div className="flex items-center justify-between mb-5">
        <p className={`text-sm ${filtered.length === 0 ? "text-rose-500" : "text-slate-500"}`}>
          {filtered.length === 0
            ? "No conditions found"
            : `Showing ${filtered.length} ${filtered.length === 1 ? "condition" : "conditions"}${activeType ? ` in ${activeTypeDef?.label ?? activeType}` : ""}${search ? ` for "${search}"` : ""}`}
        </p>
        <button
          onClick={() => setSort(sort === "az" ? "za" : "az")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border bg-white text-slate-600 border-slate-200 hover:border-rose-300 hover:text-rose-700 transition-all shrink-0"
        >
          <ArrowUpDown className="h-3 w-3" />
          {sort === "az" ? "A–Z" : "Z–A"}
        </button>
      </div>

      {/* Condition cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map(c => {
          const typeDef = TYPE_BY_KEY[c.conditionType ?? "other"] ?? TYPE_BY_KEY["other"];
          return (
            <Link key={c.id} href={`/patient/conditions/${c.id}`}>
              <Card className="h-full hover:border-rose-300 hover:shadow-md transition-all cursor-pointer rounded-2xl group">
                <CardContent className="p-6 sm:p-8 flex flex-col h-full">
                  <div className="mb-3">
                    <h2 className="text-xl font-bold text-slate-900 mb-1 group-hover:text-rose-600 transition-colors leading-snug">
                      <Highlight text={c.name} query={search} className="text-rose-700" />
                    </h2>
                    {c.simpleName && c.simpleName !== c.name && (
                      <p className="text-xs text-slate-400">Also known as: <Highlight text={c.simpleName} query={search} className="text-rose-700" /></p>
                    )}
                    {typeDef && (
                      <span className={`inline-flex items-center gap-1 mt-2 text-xs font-semibold px-2.5 py-0.5 rounded-full border ${typeDef.badgeColor}`}>
                        {typeDef.icon}
                        {typeDef.label}
                      </span>
                    )}
                  </div>

                  <p className="text-slate-600 line-clamp-3 mb-6 flex-1 text-sm leading-relaxed">
                    <Highlight text={c.patientDescription ?? ""} query={search} className="text-rose-700" />
                  </p>

                  <div className="mt-auto pt-4 border-t border-slate-100 flex items-center text-sm font-medium text-rose-600">
                    Read full guide
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}

        {filtered.length === 0 && (
          <div className="col-span-full py-16 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
            {search || activeType
              ? "No conditions found matching your filters. Try clearing the search or selecting a different type."
              : "No condition guides available right now."}
          </div>
        )}
      </div>

      {/* Cross-link to Know Your Condition */}
      <div className="mt-10 text-center">
        <Link
          href="/patient/know-your-condition"
          className="inline-flex items-center gap-2 text-sm font-medium text-rose-600 hover:text-rose-800 transition-colors"
        >
          Browse in-depth condition guides in Know Your Condition
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </PatientLayout>
  );
}
